import {
  DrawAction,
  DrawActionCodec,
  DrawActionType,
  ISwitchAction,
  IPencilAction,
  IEraserAction,
  IAddAction,
} from '@engoo/realtime-protocol/draw';
import { Trace } from '@engoo/realtime-protocol/draw/trace';
import {
  IPersistedAction,
  PersistedActionCodec,
  StreamPosition,
  makeStreamPosition,
} from '@engoo/realtime-protocol/persisted';
import { Map, OrderedMap, Record, List } from 'immutable';
import { KeyPath, parseStateKey } from 'app/models/entityStateSync/keypath';
import { EntityStringId } from 'app/types/entity';
import { IdentityEntityStringIdCodec } from 'app/types/entity/codecs';
import * as iots from 'io-ts';
import { decodeOrElse } from 'app/utils/ioTs';
import { Color } from 'app/models/rtc/draw/color';
import { Shape, Shapes, PencilShape, EraserShape, IntPoint } from 'app/models/rtc/draw/shape';
import { Resolution } from 'app/models/rtc/draw/resolution';
import { traceToIntPoints, TraceType } from 'app/models/rtc/draw/trace';
import { ActionState } from 'app/services/rtc/sync/documentmanagers';

type RequiredPersistedAction = Required<IPersistedAction> & {
  // This is a little work-around for before we get proper support
  // for Guids in RT protocol
  user: EntityStringId;
};

/**
 * DrawActionCodec with addition of hard requirement that all fields
 * from `IPersistedAction` are set
 *
 * Only a decoder for internal use. Please don't use it for encoding
 */
const PersistedDrawActionDecoder = iots.intersection([
  DrawActionCodec,
  PersistedActionCodec,
  iots.type({ user: IdentityEntityStringIdCodec }),
  // Only allow decoding
]).asDecoder();

/**
 * Most up-to-date state for a given user
 */
export class UserDrawState extends Record<{
  /**
   * Path to the current surface that a given user is drawing on
   *
   * Value comes from `switch` event.
   */
  path: KeyPath;
  /**
   * Current resolution that the given user is drawing at
   *
   * Please note that the resolution can change dynamically if
   * the surface has changed its size. This mostly applies when
   * screen-sharing a window and resizing it.
   *
   * Value comes from `switch` event.
   */
  resolution: Resolution;
  /**
   * `position` of last action that resulted in creating a shape
   * for a given user
   *
   * This is utilised to look up when last shape ended when
   * processing `add` actions. The new shape needs to continue from
   * when the last shape ended.
   */
  position: StreamPosition;
}>({
  path: List(),
  resolution: new Resolution(),
  position: makeStreamPosition(0),
}) {}

/**
 * Represents all shapes drawn on a specific surface
 */
export class Surface extends Record<{
  /**
   * Persisted shapes are only ones coming from remote in canonical
   * order.
   *
   * Once drawn on canvas, they don't need to be redrawn.
   */
  persisted: Shapes;
  /**
   * Pending shapes are either local pending shapes or remote shapes
   * received out of order.
   *
   * It has to be assumed that these shapes are either going
   * to be altered (like when simplifying lines) or drawn in a
   * different order when finally put in canonical order by the
   * realtime server.
   *
   * It's advised to snapshot the canvas before drawing them. Then
   * restore the snapshot before drawing any more persisted or
   * pending shapes.
   */
  pending: Shapes;
}>({
  persisted: OrderedMap(),
  pending: OrderedMap(),
}) {}

/**
 * All surfaces addressable by key path
 */
export type Surfaces = Map<KeyPath, Surface>;

export class DrawState extends Record<{
  version: StreamPosition;
  surfaces: Surfaces;
  userStates: Map<EntityStringId, UserDrawState>;
}>({
  version: makeStreamPosition(0),
  surfaces: Map(),
  userStates: Map(),
}) {}

const PendingStates: { [K in ActionState]: boolean } = {
  [ActionState.Confirmed]:           false,
  // Both pending (local) actions and remote actions received out
  // of order should go to the pending state because we need to
  // redraw them once we receive older actions
  [ActionState.ConfirmedOutOfOrder]: true,
  [ActionState.Pending]:             true,
};

/**
 * Draw reducer
 *
 * Only operates on persisted actions. Relies on `DrawActionHandler`
 * to create "fake" persisted actions.
 */
export const drawReducer = (
  state = new DrawState,
  action?: unknown,
  actionState: ActionState = ActionState.Pending,
): DrawState => {
  const drawAction = decodeOrElse(
    PersistedDrawActionDecoder,
    action,
    () => null,
  );

  if (!drawAction) {
    return state;
  }

  const pending = PendingStates[actionState];

  return dispatch(state, drawAction, pending).set('version', drawAction.position);
};

const dispatch = (state: DrawState, action: DrawAction & RequiredPersistedAction, pending: boolean): DrawState => {
  switch (action.type) {
    case DrawActionType.Switch:
      return switchSurface(state, action);
    case DrawActionType.Add:
      return addPoints(state, action, pending);
    case DrawActionType.Pencil:
    case DrawActionType.Eraser:
      return startShape(state, action, pending);
  }
};

const switchSurface = (state: DrawState, action: ISwitchAction & RequiredPersistedAction): DrawState => {
  const path = parseStateKey(action.path);
  const [width, height] = action.res;
  const resolution = new Resolution({ width, height });
  const { user } = action;

  return state.update('userStates', (userStates) =>
    userStates.update(
      user,
      new UserDrawState(),
      (userState) => userState.merge({ path, resolution }),
    ),
  );
};

type StartShapeAction = IPencilAction | IEraserAction;

const startShape = (
  state: DrawState,
  { user, position, ...action }: StartShapeAction & RequiredPersistedAction,
  pending: boolean,
): DrawState => {
  const userState = state.userStates.get(user);

  if (!userState) {
    return state;
  }

  const { path, resolution } = userState;

  const shape = createShape(resolution, action);

  return setShape(state, user, path, position, shape, pending);
};

const addPoints = (
  state: DrawState,
  { user, trace, position }: IAddAction & RequiredPersistedAction,
  pending: boolean,
): DrawState => {
  const userState = state.userStates.get(user);

  if (!userState) {
    return state;
  }

  const { path, position: lastPosition } = userState;

  // New shape needs to begin with the last point of the previous
  // shape
  const surface = state.surfaces.get(path);
  const lastShape =
    surface?.pending.get(lastPosition) ??
    surface?.persisted.get(lastPosition);

  if (!lastShape) {
    return state;
  }

  const shape = deriveShape(lastShape, trace);

  return setShape(state, user, path, position, shape, pending);
};

const createShape = (
  resolution: Resolution,
  action: StartShapeAction,
): Shape => {
  const points = List(traceToIntPoints(action.trace).map(x => new IntPoint(x)));

  switch (action.type) {
    case DrawActionType.Pencil:
      return new PencilShape({
        resolution,
        points,
        color: new Color(action.color),
        strokeSize: action.size,
      });
    case DrawActionType.Eraser:
      return new EraserShape({
        resolution,
        points,
        strokeSize: action.size,
      });
  }
};

const deriveShape = (shape: Shape, trace: Trace): Shape => {
  const points = shape.points.takeLast(1).concat(List(traceToIntPoints(trace).map(x => new IntPoint(x))));
  switch (shape.type) {
    // This is silly but because `Shape` is union of two classes,
    // TSC complains about `set` method not being callable
    case TraceType.Pencil: return shape.set('points', points);
    case TraceType.Eraser: return shape.set('points', points);
  }
};

const setShape = (
  state: DrawState,
  user: EntityStringId,
  path: KeyPath,
  position: StreamPosition,
  shape: Shape,
  pending: boolean,
) =>
  state.update('surfaces', surfaces =>
    surfaces.update(path, new Surface(),
      surface => surface.update(
        pending ? 'pending' : 'persisted',
        shapes => shapes.set(position, shape),
      ),
    ),
  )
  .update('userStates', (userStates) =>
    userStates.update(user, (s) => s.set('position', position)),
  );
